import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { formatLinkBlockToUrl } from '$utilities/data.js';
import { SimpleImage as Image } from '$partials/media/index.js';

/* ========================================================================= */
/* Configuration */
/* ========================================================================= */

/* Reserves space until the video reports its own dimensions */
const DEFAULT_VIDEO_RATIO = 16 / 9;

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * A grid link block that always spans the full grid width, with the asset at
 * its natural aspect ratio and the block sizing to its content.
 * @param {PropTypes.InferProps<typeof propTypes>} props
 */
const FullWidthAssetLinkBlock = ({
    caption,
    image,
    link,
    relativeUrl,
    showOnMobile = true,
    video
}) => {
    const [videoRatio, setVideoRatio] = useState(null);
    const videoRef = useRef();

    useEffect(() => {
        if (!videoRef.current) { return () => {}; }

        const measureVideoRatio = () => {
            if (videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0) {
                setVideoRatio(videoRef.current.videoWidth / videoRef.current.videoHeight);
            }
        };

        measureVideoRatio();
        videoRef.current.addEventListener('loadedmetadata', measureVideoRatio);

        return () => {
            if (videoRef.current) {
                videoRef.current.removeEventListener('loadedmetadata', measureVideoRatio);
            }
        };
    }, []);

    if (!image && !video) { return null; }

    const href = link ? formatLinkBlockToUrl(link) : relativeUrl;

    const layout = (
        <figure>
            {(image && !video) &&
                <Image
                    {...image}
                    className='w-full h-auto'
                    style={{ aspectRatio: image.width / image.height }} />
            }

            {video &&
                <video
                    autoPlay
                    className='w-full h-auto'
                    controls={false}
                    loop={true}
                    muted={true}
                    playsInline={true}
                    ref={videoRef}
                    src={video.url}
                    style={{ aspectRatio: videoRatio || DEFAULT_VIDEO_RATIO }} />
            }

            {caption &&
                <figcaption
                    className='max-w-[20em] p-layout-item sm:p-60'
                    dangerouslySetInnerHTML={{ __html: caption }} />
            }
        </figure>
    );

    return (
        <div className={`${!showOnMobile ? 'hidden sm:block' : ''} col-span-3 sm:col-span-5 md:col-span-7 lg:col-span-9 bg-foreground rounded overflow-hidden mr-1 mb-1`}>
            {href ? (
                <a href={href}>
                    {layout}
                </a>
            ) : layout}
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    caption: PropTypes.string,
    id: PropTypes.string.isRequired,
    image: PropTypes.object,
    link: PropTypes.object,
    relativeUrl: PropTypes.string,
    showOnMobile: PropTypes.bool,
    video: PropTypes.shape({
        url: PropTypes.string.isRequired
    })
};

FullWidthAssetLinkBlock.propTypes = propTypes;

export default FullWidthAssetLinkBlock;
